import { useCallback, useMemo, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import config from 'constants/config'
import moment from 'moment'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineTagAltIcon from 'LuxKit/Icons/line/LineTagAltIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import { rem } from 'polished'
import USPModal from './USPModal'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import TextLink from 'LuxKit/TextLink'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Caption from 'LuxKit/Typography/Caption'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getEffectiveCharterBundleRoute, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { useIntl } from 'react-intl'
import type { FlightsClassTypes } from 'constants/flight'
import useCharterFlightDates from 'hooks/CharterFlights/useCharterFlightDates'

const USPContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: minmax(min-content, max-content) minmax(min-content, max-content);
    align-items: flex-start;
  }
`

const USPBodyText = styled(Caption)`
  margin-left: ${rem(28)};
`

interface Props {
  offer: App.Offer
  checkIn?: string
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  availableForPurchase?: boolean
  onViewPackageOptions?: () => void
}

type ModalTypes = 'bnbl' | 'bestPrice' | 'gifting'

function FlashHotelUSPs(props: Props) {
  const { offer, checkIn, rooms, fareClass, charterBundleRoute, availableForPurchase, onViewPackageOptions } = props
  const intl = useIntl()
  const [modalType, setModalType] = useState<ModalTypes>()
  const [open, setOpen] = useState(false)
  const intlDateFormatter = useIntlDateFormatter()

  const onItemClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const modalType = e.currentTarget.dataset.type as ModalTypes
    setModalType(modalType)
    setOpen(true)
  }, [])

  const onClose = useCallback(() => {
    setOpen(false)
  }, [])

  const hasBNBL = offer.hasBuyNowBookLaterPackages && offer.hasBuyNowBookLater && offer.travelToDate && config.accommodation.hasBnbl
  const isSevenDaysBeforeTravelToDate = moment().add(1, 'w').isBefore(offer.travelToDate)
  const isMemberOnlyOffer = offer.luxPlus.access === 'memberOnly'

  const bookBy = useMemo(() => moment(offer.bookByDate), [offer.bookByDate])
  const travelUntil = useMemo(() => moment(offer.travelToDate), [offer.travelToDate])

  const { departDate: charterDepartDate } = useCharterFlightDates({
    checkInDate: checkIn,
    nights: offer.lowestPricePackage?.duration,
    charterBundleRoute,
    rooms,
    fareClass,
  })

  const bestPriceText = intl.formatMessage({
    id: 'MRdkDgY2YvcfVWLDrcaV',
    defaultMessage: 'We price match',
    description: 'Unique selling point text for the price match guarantee displayed on offer pages',
  })

  return (
    <>
      <USPContainer>
        {availableForPurchase && <FlashCancellationPolicies
          checkInDate={checkIn}
          cancellationReferenceDate={charterBundleRoute ? charterDepartDate : undefined}
          propertyTimezoneOffset={offer?.property?.timezoneOffset}
          cancellationPolicy={offer?.lowestPricePackage?.roomRate?.cancellationPolicy}
          isFlashUSP
        />}
        {!offer.bundledWithFlightsOnly && config.bestPriceGuarantee && !offer.disableBestPriceGuarantee && (
          <TextLink
            size="medium"
            colour="highlight-primary-dark"
            underline={false}
            data-type="bestPrice"
            onClick={onItemClick}
            startIcon={<LineTagAltIcon />}
          >
            {bestPriceText}
          </TextLink>
        )}
        {hasBNBL && <div>
          <TextLink
            size="medium"
            colour="highlight-primary-dark"
            underline={false}
            data-type="bnbl"
            data-testid="usp-bnbl"
            onClick={onItemClick}
            startIcon={<LineCalendarIcon />}
          >
            {intl.formatMessage({
              id: 'mglfGD7L6Y2B_A0q-4us',
              defaultMessage: 'Buy now, choose dates later',
              description: 'Text for buy now choose dates later USP in the date selection or calendar picker',
            })}
          </TextLink>
          <USPBodyText colour="light-foreground-alt" variant="medium">
            {intl.formatMessage(
              {
                id: 'COIUdQnhEdI9HSyLDOy0',
                defaultMessage: 'Travel until {date}',
                description: 'Text showing travel until date in the date selection or calendar picker',
              },
              {
                date: intlDateFormatter(travelUntil, INTL_DMY_CASUAL_FORMAT),
              },
            )}
          </USPBodyText>
        </div>}
        {hasBNBL && isSevenDaysBeforeTravelToDate && !isMemberOnlyOffer &&
          <TextLink
            size="medium"
            colour="highlight-primary-dark"
            underline={false}
            data-type="gifting"
            onClick={onItemClick}
            startIcon={<LineGiftIcon />}
          >
            {intl.formatMessage({
              id: 'q-sWbOrCqD3itZ0uPTo_',
              defaultMessage: 'Gifting available',
              description: 'Text for gifting available USP on the gift card purchase or redemption page',
            })}
          </TextLink>}
      </USPContainer>
      <USPModal open={open} onClose={onClose} modalType={modalType} bookBy={bookBy} travelUntil={travelUntil} onViewPackageOptions={onViewPackageOptions} />
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  availableForPurchase: isAvailableForPurchase(state, appState),
  rooms: state.rooms,
  fareClass: state.fareClass,
  charterBundleRoute: getEffectiveCharterBundleRoute(state),
}), FlashHotelUSPs)
